import { apiUrl, responseError } from "./api";
import type { DictaPlan, DictaPlanId } from "../domain/plans";

const STAGING_DEMO = import.meta.env.VITE_STAGING_DEMO === "true";

export type UsageSummary = {
  periodKey: string;
  includedSeconds: number;
  usedSeconds: number;
  remainingSeconds: number;
};

export type UserSettings = {
  defaultJurisdictionPackId: string | null;
  emailDeliveryReceipts: boolean;
  autoDeleteAudioWithin24Hours: boolean;
};

export type BillingSubscription = {
  status: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
};

export type DictaAccount = {
  user: {
    id: string;
    email: string;
    plan: DictaPlanId;
    name: string | null;
    createdAt: string;
  };
  settings: UserSettings;
  plans: readonly DictaPlan[];
  usage: UsageSummary;
  promotions: {
    redeemedCodes: string[];
    lifetimeBonusSeconds: number;
    lifetimeBonusRemainingSeconds: number;
  };
  billing: {
    mode: "test" | "live";
    checkoutEnabled: boolean;
    subscription: BillingSubscription | null;
  };
};

export type AccountProbe =
  | { mode: "remote"; account: DictaAccount | null }
  | { mode: "local"; account: null };

export const probeAccount = async (): Promise<AccountProbe> => {
  try {
    const response = await fetch(apiUrl("/api/auth/session"), {
      credentials: "same-origin"
    });
    if (response.status === 401) return { mode: "remote", account: null };
    // Local prototype mode exists only for the Vite dev server pointing at
    // the Node backend. Production builds fail closed: an outage or network
    // error must never downgrade the app to unauthenticated local mode.
    if (response.status === 404 && import.meta.env.DEV && !STAGING_DEMO) {
      return { mode: "local", account: null };
    }
    if (!response.ok) {
      throw await responseError(response, "Account session could not be loaded.");
    }
    return {
      mode: "remote",
      account: (await response.json()) as DictaAccount
    };
  } catch {
    return import.meta.env.DEV && !STAGING_DEMO
      ? { mode: "local", account: null }
      : { mode: "remote", account: null };
  }
};

export const beginAuth = (
  mode: "signin" | "signup",
  options: {
    returnTo?: string;
    provider?: "authkit" | "GoogleOAuth" | "MicrosoftOAuth";
  } = {}
) => {
  const screenHint = mode === "signup" ? "sign-up" : "sign-in";
  const params = new URLSearchParams({ screen_hint: screenHint });
  if (options.returnTo?.startsWith("/")) params.set("return_to", options.returnTo);
  if (options.provider) params.set("provider", options.provider);
  window.location.assign(apiUrl(`/api/auth/start?${params.toString()}`));
};

export const logoutAccount = async () => {
  const response = await fetch(apiUrl("/api/auth/logout"), {
    method: "POST",
    credentials: "same-origin"
  });
  if (!response.ok) throw await responseError(response, "Sign out failed.");
  const result = (await response.json()) as { logoutUrl?: unknown };
  window.location.assign(
    typeof result.logoutUrl === "string" && result.logoutUrl
      ? result.logoutUrl
      : "/"
  );
};

export const selectAccountPlan = async (
  plan: DictaPlanId
): Promise<DictaAccount> => {
  const response = await fetch(apiUrl("/api/account/plan"), {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ plan })
  });
  if (!response.ok) throw await responseError(response, "Plan could not be changed.");
  return (await response.json()) as DictaAccount;
};

export const redeemPromotion = async (code: string): Promise<DictaAccount> => {
  const response = await fetch(apiUrl("/api/account/promotions/redeem"), {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code })
  });
  if (!response.ok) {
    throw await responseError(response, "Promotion could not be redeemed.");
  }
  return (await response.json()) as DictaAccount;
};

/** Starts Stripe Checkout: the browser leaves for Stripe's hosted page. */
export const startCheckout = async (): Promise<void> => {
  const response = await fetch(apiUrl("/api/billing/checkout"), {
    method: "POST",
    credentials: "same-origin"
  });
  if (!response.ok) {
    throw await responseError(response, "Checkout could not be started.");
  }
  const { url } = (await response.json()) as { url: string };
  window.location.assign(url);
};

/** Opens the Stripe Customer Portal for card changes and cancellation. */
export const openBillingPortal = async (): Promise<void> => {
  const response = await fetch(apiUrl("/api/billing/portal"), {
    method: "POST",
    credentials: "same-origin"
  });
  if (!response.ok) {
    throw await responseError(response, "Billing portal could not be opened.");
  }
  const { url } = (await response.json()) as { url: string };
  window.location.assign(url);
};

/** Confirms a Checkout success return before the webhook may have landed. */
export const confirmCheckout = async (
  sessionId: string
): Promise<DictaAccount> => {
  const response = await fetch(apiUrl("/api/billing/confirm"), {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sessionId })
  });
  if (!response.ok) {
    throw await responseError(response, "Subscription could not be confirmed.");
  }
  return (await response.json()) as DictaAccount;
};

export const updateAccountSettings = async (update: {
  name?: string | null;
  settings?: Partial<UserSettings>;
}): Promise<DictaAccount> => {
  const response = await fetch(apiUrl("/api/account/settings"), {
    method: "PUT",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(update)
  });
  if (!response.ok) {
    throw await responseError(response, "Settings could not be saved.");
  }
  return (await response.json()) as DictaAccount;
};

/** Revokes every session for the account, including the current one. */
export const revokeAllSessions = async (): Promise<void> => {
  const response = await fetch(apiUrl("/api/account/sessions/revoke"), {
    method: "POST",
    credentials: "same-origin"
  });
  if (!response.ok) {
    throw await responseError(response, "Sessions could not be revoked.");
  }
};

export const formatAllowance = (seconds: number) => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = minutes / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hr`;
};
